import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { type ExperienceCategory, useCategoryCache } from './useCategoryCache';
import type {
  UpdateExperienceCategorySortOrderMutation,
  UpdateExperienceCategorySortOrderMutationVariables,
} from './useUpdateCategorySortOrder.generated';

const query = gql`
  mutation UpdateExperienceCategorySortOrder(
    $input: UpdateExperienceCategorySortOrderInput!
  ) {
    updateExperienceCategorySortOrder(input: $input)
  }
`;

export const useUpdateCategorySortOrder = () => {
  const experience = useEffectiveExperience();
  const cache = useCategoryCache();

  const { mutate, ...rest } = useMutation<
    UpdateExperienceCategorySortOrderMutation,
    QueryError,
    UpdateExperienceCategorySortOrderMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateExperienceCategorySortOrderMutation,
        UpdateExperienceCategorySortOrderMutationVariables
      >(query, variables),
  });

  const updateSortOrder = (
    categories: ExperienceCategory[],
    options?: MutateOptions<
      UpdateExperienceCategorySortOrderMutation,
      QueryError,
      UpdateExperienceCategorySortOrderMutationVariables
    >
  ) => {
    return mutate(
      {
        input: {
          experienceId: experience.id,
          categories: categories.map((category, index) => ({
            id: category.id,
            sortOrder: index,
          })),
        },
      },
      {
        ...options,
        onSuccess: (...args) => {
          cache.setSortOrder(categories);
          options?.onSuccess?.(...args);
        },
      }
    );
  };

  return {
    updateSortOrder,
    ...rest,
  };
};
